import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Row,
  TreeDataNode,
  Typography,
  Space,
  Card,
  message,
} from "antd";
import { CarryOutOutlined, PlusOutlined } from "@ant-design/icons";

import CustomCard from "../../../../../../components/custom-card/CustomCard";
import DetailChapter from "./detail-chapter/DetailChapter";
import {
  TypeCourseStructure,
  TypeChapterList,
  TypeSubjectList,
  TypeCoursePlanAssignment,
  moocCoursePlanAssignment,
} from "../../../../../../types/course-plan-managment/mooc-course-construction-plan";
import {
  getListMoocCourseDescriptionDocumentById,
  getListMoocCourseStructureById,
} from "../../../../../../service/course-plan-managment/planning-course-construction/PlanningCourseConstructionApi";

interface CourseStructureProps {
  dataCourseStructure: TypeChapterList[];
  setDataCourseStructure: (data: TypeChapterList[]) => void;
  detailData: any;
}

const CourseStructure: React.FC<CourseStructureProps> = ({
  dataCourseStructure,
  setDataCourseStructure,
  detailData,
}) => {
  const { t } = useTranslation();
  // log removed
  const [expandAll, setExpandAll] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<any[]>([]);
  const handleClickExpand = () => {
    setExpandAll(!expandAll);
  };
  useEffect(() => {
    if (expandAll) {
      setExpandedKeys(["0-0", "0-0-0"]);
    } else {
      setExpandedKeys([]);
    }
  }, [expandAll]);
  const onSelect = (selectedKeys: React.Key[], info: any) => {
    console.log("selected", selectedKeys, info);
  };

  const updateChapterList = (newChapterList: TypeChapterList[]) => {
    setDataCourseStructure(newChapterList);
  };

  interface CourseItem {
    id: number;
    courseConstructionPlanId?: number;
    structureItemName?: string;
    structureItemDescription?: string;
    structureItemLevel?: number;
    coursewareType?: number;
    orderNumber?: number;
    parentId?: number;
    createdDate?: Date;
    updatedDate?: Date;
    isDeleted?: boolean;
    subjectList: TypeSubjectList[];
    moocCoursePlanAssignment?: moocCoursePlanAssignment[];
  }

  const normalizeData = (data: CourseItem[]): TypeChapterList[] => {
    const itemsById: Record<number, CourseItem> = {};

    // Initialize structure with empty subjectList and lessonList
    data.forEach((item) => {
      itemsById[item.id] = {
        ...item,
        subjectList: item.subjectList || [],
      };
    });

    // Create hierarchical structure
    data.forEach((item) => {
      if (item.parentId) {
        const parent = itemsById[item.parentId];
        if (parent) {
          if (item.structureItemLevel === 2) {
            parent.subjectList.push({
              ...item,
              lessonList: [],
            } as TypeSubjectList);
          } else if (item.structureItemLevel === 3) {
            // Here we should find the correct subject and add the lesson to its lessonList
            Object.values(itemsById).forEach((potentialParent) => {
              if (potentialParent.structureItemLevel === 1) {
                const subject = potentialParent.subjectList.find(
                  (sub) => sub.id === item.parentId
                );
                if (subject) {
                  subject.lessonList.push(item as TypeCourseStructure);
                }
              }
            });
          }
        }
      }
    });

    // Filter to return only top-level items (Chapters)
    const topLevelItems: TypeChapterList[] = Object.values(itemsById)
      .filter((item) => item.parentId === null && item.structureItemLevel === 1)
      .map((item) => ({
        ...item,
        moocCoursePlanAssignment: item.moocCoursePlanAssignment
          ? item.moocCoursePlanAssignment.map((assignment) => ({
              ...assignment,
              isDeleted: assignment.isDeleted || false, // Ensure boolean value
            }))
          : [],
      })) as TypeChapterList[];

    return topLevelItems;
  };

  const synchronizeMoocStructure = async () => {
    try {
      const response = await getListMoocCourseStructureById(
        detailData.registration_stage_id
      );
      if (response && response.data) {
        const normalizedData = normalizeData(response.data.data);
        console.log("đây là normalizedData", normalizeData);
        setDataCourseStructure(normalizedData);

        message.success(t("Đồng bộ cấu trúc thành công!", "Đồng bộ cấu trúc thành công!"));
      } else {
        throw new Error("Invalid data format received from API");
      }
    } catch (error) {
      console.error("Error in synchronizeMoocStructure: ", error);
      message.error(`Error synchronizing structure: ${error}`);
    }
  };

  const handleAddNewChapter = () => {
    const newChapter: TypeChapterList = {
      structureItemName: t("Chương mới", "Chương mới"),
      structureItemLevel: 1,
      orderNumber: dataCourseStructure ? dataCourseStructure.length + 1 : 1, // Tính toán số thứ tự chương mới
      subjectList: [
        {
          structureItemName: t("Tên bài giảng", "Tên bài giảng"),
          structureItemLevel: 2,
          orderNumber: 1,
          lessonList: [
            {
              structureItemName: t("Tên học liệu", "Tên học liệu"),
              structureItemLevel: 3,
              orderNumber: 1,
            },
          ],
        },
      ],
    };

    // Thêm chương mới vào danh sách chapterList
    const newChapterList = [...(dataCourseStructure || []), newChapter];

    // Gọi hàm cập nhật chapterList mới
    updateChapterList && updateChapterList(newChapterList);
  };

  const [isShowDetailAll, setIsShowDetailAll] = useState<boolean>(true);
  const handleToggleShowDetailAll = () => {
    setIsShowDetailAll(!isShowDetailAll);
  };

  return (
    <div className="course-managment-plan-course-structure">
      <Space
        size="middle"
        direction="vertical"
        style={{ width: "100%", marginTop: "-25px" }}
        className="spaceTag"
      >
        <Card style={{ marginBottom: "20px", minWidth: "680px" }}>
          <Row justify="space-between" style={{ marginTop: "20px" }}>
            <Col>
              <Row>
                <Typography.Title level={4}>{t("Cấu trúc của khóa học", "Cấu trúc của khóa học")}</Typography.Title>
              </Row>
              <Row>
                <Typography.Text style={{ color: "GrayText" }}>{t("Xây dựng cấu trúc của khoá học", "Xây dựng cấu trúc của khoá học")}</Typography.Text>
              </Row>
            </Col>
            <Col>
              <Button onClick={synchronizeMoocStructure}>
                {/* {isShowDetailAll ? t("Thu gọn", "Thu gọn") : t("Hiển thị chi tiết", "Hiển thị chi tiết")} */}
                {t("Đồng bộ cấu trúc", "Đồng bộ cấu trúc")}
              </Button>
            </Col>
          </Row>
        </Card>

        <DetailChapter
          // chapterList={chapterList}
          chapterList={dataCourseStructure}
          updateChapterList={updateChapterList}
          isShowDetailAll={isShowDetailAll}
          setIsShowDetailAll={setIsShowDetailAll}
        />

        {/* thêm chương */}
        <>
          <Button
            type="primary"
            onClick={handleAddNewChapter}
            className="w-full button-sticky"
          >
            <PlusOutlined /> {t("Thêm chương", "Thêm chương")}
          </Button>
        </>
      </Space>
    </div>
  );
};

export default CourseStructure;
